import { supabase } from '@/core/database/supabase';

export interface PermissionContext {
  companyId?: string;
  branchId?: string;
  warehouseId?: string;
  projectId?: string;
  ownerUserId?: string;
  requesterUserId?: string;
  amount?: number;
  value?: number;
  [key: string]: unknown;
}

export interface PermissionDecision {
  allowed: boolean;
  reason: string;
  matchedRoleIds: string[];
  matchedPolicyIds: string[];
}

const v_role_cache = new Map<string, string>();

export const permissionEngine = {
  async can(userId: string, permissionKey: string, context: PermissionContext = {}): Promise<PermissionDecision> {
    // 1. Kiểm tra role_code từ cache hoặc profile để bypass nhanh
    try {
      let roleCode = v_role_cache.get(userId);
      
      if (!roleCode) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role_code')
          .eq('id', userId)
          .maybeSingle();
        
        if (profile?.role_code) {
          roleCode = profile.role_code;
          v_role_cache.set(userId, roleCode);
        }
      }

      if (roleCode === 'super_admin' || roleCode === 'admin') {
        return {
          allowed: true,
          reason: 'BYPASS_ADMIN',
          matchedRoleIds: [],
          matchedPolicyIds: [],
        };
      }
    } catch (err) {
      console.warn('[permissionEngine] Failed to fetch role for bypass:', err);
    }

    // 2. Gọi RPC fn_has_permission để kiểm tra chính xác từ DB
    const { data, error } = await (supabase as any).rpc('fn_has_permission', {
      p_user_id: userId,
      p_permission_key: permissionKey,
      p_context: context,
    });

    if (error) {
      return {
        allowed: false,
        reason: error.message || 'PERMISSION_RPC_ERROR',
        matchedRoleIds: [],
        matchedPolicyIds: [],
      };
    }

    const row = Array.isArray(data) ? data[0] : data;
    return {
      allowed: Boolean(row?.allowed),
      reason: String(row?.reason || 'UNKNOWN'),
      matchedRoleIds: Array.isArray(row?.matched_role_ids) ? row.matched_role_ids : [],
      matchedPolicyIds: Array.isArray(row?.matched_policy_ids) ? row.matched_policy_ids : [],
    };
  },

  async assert(userId: string, permissionKey: string, context: PermissionContext = {}) {
    const decision = await this.can(userId, permissionKey, context);
    if (!decision.allowed) {
      throw new Error(`Không có quyền ${permissionKey}: ${decision.reason}`);
    }
    return decision;
  },
};
